/**
 * Global PayPal Provider
 * Wraps the entire app with a single PayPalScriptProvider to avoid multiple provider issues
 */

import React from 'react';
import { PayPalScriptProvider } from '@paypal/react-paypal-js';
import { detectProductionEnvironment, getPayPalClientId } from '@/features/billing/services/auth/environment';

interface PayPalProviderProps {
  children: React.ReactNode;
}

export const PayPalProvider: React.FC<PayPalProviderProps> = ({ children }) => {
  const [isProduction] = React.useState(detectProductionEnvironment());
  const [paypalClientId] = React.useState(() => getPayPalClientId(isProduction));

  // If PayPal is not configured, just render children without the provider
  if (!paypalClientId) {
    return <>{children}</>;
  }

  return (
    <PayPalScriptProvider
      options={{
        clientId: paypalClientId,
        currency: 'USD',
        components: 'buttons', // Load all button types
        vault: true, // REQUIRED: Save payment method for future charges (subscriptions/auto-renewal)
        intent: 'subscription', // REQUIRED: For recurring payments/subscriptions
        disableFunding: ['credit', 'card'] // Hide non-PayPal funding sources
      }}
    >
      {children}
    </PayPalScriptProvider>
  );
};

export default PayPalProvider;
